import { SenAnalysisFeatureService } from '@/features/sen-analysis/sen-analysis-feature.service';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { AsyncPipe, NgClass, NgStyle } from '@angular/common';
import { Component, Input, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import moment from 'moment';
import type { MultiStateSensorsTileData, MultiStateTileSensorDisplayParameters, MultiTileSensor } from '../../../interfaces/Tile.interface';
import { SensorPipe } from '../../../pipes/sensor.pipe';
import { TilesProvider } from '../../../providers/tiles/tiles.provider';
import { EditTextComponent } from '../../form-controls/controls/edit-text/edit-text';
import { SpawnableTile } from '../spawnable-tile/spawnable-tile.component';

type MultiStateTileSensorsData = MultiTileSensor<MultiStateTileSensorDisplayParameters>;

@Component({
    styleUrls: ['./tile-multi-state-sensors.scss'],
    selector: 'tile-multi-state-sensors',
    templateUrl: 'tile-multi-state-sensors.html',
    imports: [UiSectionComponent, UiSectionContentComponent, EditTextComponent, FormsModule, NgClass, NgStyle, AsyncPipe, SensorPipe]
})
export class TileMultiStateSensorsComponent extends SpawnableTile<MultiStateSensorsTileData> {
    private _tilesProvider = inject(TilesProvider);
    private _senAnalysisService = inject(SenAnalysisFeatureService);

    @Input() tile!: MultiStateSensorsTileData;

    getSensorParamToDisplay(sensorInfo: MultiStateTileSensorsData): { value: string | null; color: string | null } {
        const sensorIdentifier = sensorInfo.sensorIdentifier;
        const values = this.tile.value;
        const sensorValueIdentifier =
            values && Array.isArray(values)
                ? values.find(
                      value =>
                          value.sensorIdentifier.sensorId === sensorIdentifier.sensorId &&
                          value.sensorIdentifier.systemId === sensorIdentifier.systemId
                  )
                : null;

        let value: string | null = null;
        let color: string | null = null;

        // Display '--' if the value is too old (> 24 h) for intensive sensor
        if (!sensorValueIdentifier || moment().diff(moment(sensorValueIdentifier.timestamp), 'days', true) > 1) {
            value = null;
        } else {
            color =
                sensorValueIdentifier.value >= 0.5
                    ? sensorInfo.displayParameters.highValueColorHex
                    : sensorInfo.displayParameters.lowValueColorHex;
            value =
                sensorValueIdentifier.value >= 0.5
                    ? sensorInfo.displayParameters.highValueLabel
                    : sensorInfo.displayParameters.lowValueLabel;
        }
        return { value, color };
    }

    async goToNext(tile: MultiStateSensorsTileData) {
        if (tile) {
            this._senAnalysisService.goToAnalysis(
                tile.buildingId,
                tile.description.sensors.map(sensor => sensor.timeseriesId)
            );
        }
    }

    updateTileName() {
        this._tilesProvider.renameTile(this.tile.tileId, this.tile.name);
    }
}
